import React, { useEffect, useState } from 'react';
import { Panel, FlexboxGrid, Button, Message, Loader } from 'rsuite';
import { isMobile } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './Auth/AuthContext';
import { usePreferences } from './Preferences/PreferencesContext';
import { getThemeColors } from '../helpers/themeColors';
import { API_BASE } from '../helpers/authFetch';

import NavigationBar from './Shared/NavigationBar';

const SPACING = {
  containerMaxWidth: 1100,
  containerPadding: 24,
  containerPaddingMobile: 12,
  heroMarginTop: 40,
  heroMarginBottom: 32,
  sectionMarginBottom: 40,
  gridGap: 20,
  featureCardPadding: 20,
  emojiSize: 32,
  emojiMarginBottom: 8,
  sectionLabelMarginBottom: 16,
  randomCardMaxWidth: 400,
  randomCardImageMaxHeight: 400,
  randomCardImageMaxHeightMobile: 300,
  randomCardImageBorderRadius: 12,
  randomCardNameMarginTop: 12,
  randomCardLoaderHeight: 400,
  randomCardLoaderHeightMobile: 300,
  ctaPadding: 32,
  ctaPaddingMobile: 20,
  ctaBorderWidth: 1,
  ctaBorderRadius: 8,
  ctaButtonGap: 12,
  shuffleButtonGap: 10,
  shuffleButtonMinSize: 44,
};

const FONT = {
  heroTitle: 36,
  heroTitleMobile: 26,
  heroTagline: 18,
  heroTaglineMobile: 14,
  sectionLabel: 22,
  sectionLabelMobile: 18,
  featureTitle: 16,
  featureDescription: 13,
  lockLabel: 11,
  ctaHeading: 22,
  ctaHeadingMobile: 18,
  ctaBody: 14,
  disclaimer: 12,
  randomCardName: 18,
  randomCardMeta: 13,
};

const buildColors = (theme) => {
  const t = getThemeColors(theme);
  return {
    heroTitle: t.textPrimary,
    heroTagline: t.textMuted,
    sectionLabel: t.textPrimary,
    featureTitle: t.textPrimary,
    featureDescription: t.textMuted,
    lockLabel: '#f0ad4e',
    featureCardBg: t.cardBackground,
    featureCardBorder: t.borderPrimary,
    ctaBg: t.backgroundSecondary,
    ctaBorder: t.accentSecondary,
    ctaHeading: t.textPrimary,
    ctaText: t.textSecondary,
    ctaSecondary: t.accentPrimary,
    randomCardName: t.textPrimary,
    randomCardMeta: t.textFaint,
    randomCardShadow: theme === 'light' ? 'rgba(0, 0, 0, 0.15)' : 'rgba(0, 0, 0, 0.4)',
  };
};

const FEATURE_CARDS = [
  {
    emoji: '🔍',
    title: 'Card Search',
    description: 'Find any card with fuzzy search by name and autocomplete suggestions.',
    requiresAuth: false,
  },
  {
    emoji: '📚',
    title: 'Set Browser',
    description: 'Explore expansions, masters sets, and core sets with full card listings.',
    requiresAuth: false,
  },
  {
    emoji: '📋',
    title: 'Card Details',
    description: 'View pricing, format legality, oracle text, rulings, and purchase links.',
    requiresAuth: false,
  },
  {
    emoji: '🗂️',
    title: 'Collection Tracker',
    description: 'Organize your cards in trade binders and manage your decks.',
    requiresAuth: true,
  },
  {
    emoji: '🏗️',
    title: 'Deck Builder',
    description: 'Build decks with format validation for Commander, Standard, Modern, and more.',
    requiresAuth: true,
  },
  {
    emoji: '📤',
    title: 'Import / Export',
    description: 'Import collections via CSV or paste decklists. Export your data anytime.',
    requiresAuth: true,
  },
];

const GRID_COLUMNS = {
  desktop: 3,
  tablet: 2,
  mobile: 1,
};

const getImageUri = (card) => {
  if (card?.image_uris?.normal) return card.image_uris.normal;
  if (card?.card_faces?.[0]?.image_uris?.normal) return card.card_faces[0].image_uris.normal;
  return null;
};

const DISCLAIMER_TEXT =
  'Market values displayed throughout this app are sourced from third-party data ' +
  '(Scryfall/TCGPlayer) and are approximations only. Prices should not be treated as a ' +
  'guaranteed evaluation of any card or collection. Actual market value may vary based on ' +
  'card condition, availability, and market fluctuations. This app is not a financial ' +
  'advisor. Always do your own research before making buying or selling decisions.';

/**
 * Renders the home page of the website
 * @returns React Component
 */
const Home = () => {
  const { isAuthenticated } = useAuth();
  const { preferences } = usePreferences();
  const navigate = useNavigate();
  const [randomCard, setRandomCard] = useState(null);
  const [shuffleLoading, setShuffleLoading] = useState(false);
  const COLORS = buildColors(preferences.theme);

  const fetchRandomCard = async () => {
    const results = await fetch(`${API_BASE}/cards/random`);
    const data = await results.json();
    setRandomCard(data);
  };

  const handleShuffle = async () => {
    setShuffleLoading(true);
    await fetchRandomCard();
    setShuffleLoading(false);
  };

  useEffect(() => {
    fetchRandomCard();
  }, []);

  const columns = isMobile ? GRID_COLUMNS.mobile : GRID_COLUMNS.desktop;
  const columnWidth = Math.floor(24 / columns);

  return (
    <>
      <NavigationBar />
      <div style={{
        maxWidth: SPACING.containerMaxWidth,
        margin: '0 auto',
        padding: isMobile ? SPACING.containerPaddingMobile : SPACING.containerPadding,
      }}>
        {/* Hero Section */}
        <div style={{
          textAlign: 'center',
          marginTop: SPACING.heroMarginTop,
          marginBottom: SPACING.heroMarginBottom,
        }}>
          <h1 style={{
            fontSize: isMobile ? FONT.heroTitleMobile : FONT.heroTitle,
            color: COLORS.heroTitle,
            marginBottom: 8,
          }}>
            CollectNDeck
          </h1>
          <p style={{
            fontSize: isMobile ? FONT.heroTaglineMobile : FONT.heroTagline,
            color: COLORS.heroTagline,
            margin: 0,
          }}>
            Your complete Unofficial Magic: The Gathering companion
          </p>
        </div>

        {/* Random Card Section */}
        <div style={{ marginBottom: SPACING.sectionMarginBottom }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: SPACING.shuffleButtonGap,
            marginBottom: SPACING.sectionLabelMarginBottom,
          }}>
            <h3 style={{
              fontSize: isMobile ? FONT.sectionLabelMobile : FONT.sectionLabel,
              color: COLORS.sectionLabel,
              margin: 0,
            }}>
              🎲 Random Card
            </h3>
            <Button
              appearance="subtle"
              loading={shuffleLoading}
              onClick={handleShuffle}
              style={{
                minWidth: SPACING.shuffleButtonMinSize,
                minHeight: SPACING.shuffleButtonMinSize,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              🔀
            </Button>
          </div>
          <div style={{ maxWidth: SPACING.randomCardMaxWidth, margin: '0 auto', textAlign: 'center' }}>
            {!randomCard ? (
              <div style={{
                height: isMobile ? SPACING.randomCardLoaderHeightMobile : SPACING.randomCardLoaderHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Loader size="md" content="Loading..." />
              </div>
            ) : (
              <div
                onClick={() => navigate(`/cardsearch/${randomCard.id}`)}
                style={{ cursor: 'pointer', display: 'inline-block' }}
              >
                {getImageUri(randomCard) && (
                  <img
                    src={getImageUri(randomCard)}
                    alt={randomCard.name}
                    style={{
                      maxHeight: isMobile ? SPACING.randomCardImageMaxHeightMobile : SPACING.randomCardImageMaxHeight,
                      width: 'auto',
                      borderRadius: SPACING.randomCardImageBorderRadius,
                      boxShadow: `0 4px 20px ${COLORS.randomCardShadow}`,
                      transition: 'transform 0.2s, brightness 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                  />
                )}
                <div style={{
                  fontSize: FONT.randomCardName,
                  color: COLORS.randomCardName,
                  fontWeight: 600,
                  marginTop: SPACING.randomCardNameMarginTop,
                }}>
                  {randomCard.name}
                </div>
                <div style={{
                  fontSize: FONT.randomCardMeta,
                  color: COLORS.randomCardMeta,
                  marginTop: 4,
                }}>
                  {randomCard.type_line}{randomCard.set_name ? ` · ${randomCard.set_name}` : ''}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Feature Highlights Section */}
        <div style={{ marginBottom: SPACING.sectionMarginBottom }}>
          <h3 style={{
            fontSize: isMobile ? FONT.sectionLabelMobile : FONT.sectionLabel,
            color: COLORS.sectionLabel,
            marginBottom: SPACING.sectionLabelMarginBottom,
            textAlign: 'center',
          }}>
            Features
          </h3>
          <FlexboxGrid justify="start" style={{ gap: SPACING.gridGap }}>
            {FEATURE_CARDS.map((feature) => (
              <FlexboxGrid.Item
                key={feature.title}
                colspan={columnWidth}
                style={{
                  flex: `0 0 calc(${100 / columns}% - ${SPACING.gridGap * (columns - 1) / columns}px)`,
                  maxWidth: `calc(${100 / columns}% - ${SPACING.gridGap * (columns - 1) / columns}px)`,
                }}
              >
                <Panel
                  bordered
                  style={{
                    backgroundColor: COLORS.featureCardBg,
                    borderColor: COLORS.featureCardBorder,
                    padding: SPACING.featureCardPadding,
                    height: '100%',
                  }}
                >
                  <div style={{ fontSize: SPACING.emojiSize, marginBottom: SPACING.emojiMarginBottom }}>
                    {feature.emoji}
                  </div>
                  <div style={{
                    fontSize: FONT.featureTitle,
                    color: COLORS.featureTitle,
                    fontWeight: 600,
                    marginBottom: 6,
                  }}>
                    {feature.title}
                    {feature.requiresAuth && (
                      <span style={{
                        fontSize: FONT.lockLabel,
                        color: COLORS.lockLabel,
                        marginLeft: 8,
                        fontWeight: 400,
                      }}>
                        🔒 Account required
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontSize: FONT.featureDescription,
                    color: COLORS.featureDescription,
                    lineHeight: 1.5,
                  }}>
                    {feature.description}
                  </div>
                </Panel>
              </FlexboxGrid.Item>
            ))}
          </FlexboxGrid>
        </div>

        {/* CTA Banner - unauthenticated users only */}
        {!isAuthenticated && (
          <div style={{
            backgroundColor: COLORS.ctaBg,
            border: `${SPACING.ctaBorderWidth}px solid ${COLORS.ctaBorder}`,
            borderRadius: SPACING.ctaBorderRadius,
            padding: isMobile ? SPACING.ctaPaddingMobile : SPACING.ctaPadding,
            marginBottom: SPACING.sectionMarginBottom,
            textAlign: 'center',
          }}>
            <h3 style={{
              fontSize: isMobile ? FONT.ctaHeadingMobile : FONT.ctaHeading,
              color: COLORS.ctaHeading,
              marginBottom: 12,
            }}>
              Unlock Your Full Collection Experience
            </h3>
            <p style={{
              fontSize: FONT.ctaBody,
              color: COLORS.ctaText,
              lineHeight: 1.6,
              marginBottom: 20,
              maxWidth: 600,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}>
              Create a free account to track your collection, build and validate decks across formats like Commander, Standard, and Modern, import and export decklists, and share your collections with friends.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: SPACING.ctaButtonGap }}>
              <Button appearance="primary" onClick={() => navigate('/register')}>
                Create Account
              </Button>
              <Button appearance="link" style={{ color: COLORS.ctaSecondary }} onClick={() => navigate('/login')}>
                Sign In
              </Button>
            </div>
          </div>
        )}

        {/* Market Value Disclaimer */}
        <Message type="info" style={{ marginBottom: SPACING.sectionMarginBottom }}>
          <span style={{ fontSize: FONT.disclaimer, lineHeight: 1.6 }}>
            {DISCLAIMER_TEXT}
          </span>
        </Message>
      </div>
    </>
  );
};

export default Home;
